/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router';

import Swal from 'sweetalert2';
import { Modal, ModalBody } from 'reactstrap';
import { Button, Container, Card, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import DaumPostcode from 'react-daum-postcode';
import moment from 'moment';

import SimpleHeader from 'components/Headers/SimpleHeader';
import ReactDatePicker from 'components/DatePicker/ReactDatePicker';

import { signup, getGroupList, duplicatedId, duplicatedEmail, duplicatedPhone } from 'apis/admin/userApi';

function CreateMember(props) {
  const [groupList, setGroupList] = useState([]);
  const [show, setShow] = useState(false);
  const [birthday, setBirthday] = useState();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
  });

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const getGroups = () => {
    getGroupList()
      .then(response => {
        if (response.data.success) {
          setGroupList(response.data.result);
          response.data.result.forEach(element => {
            if (element.is_default === 1) {
              setValue('member_group', element.id);
            }
          });
        }
      })
      .catch(error => {
        console.error('error occured in CreateMember.js - getGroupList() in useEffect ', error);

        Swal.fire({
          icon: 'error',
          title: '그룹 목록을 불러올 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
        }).then(() => {
          return;
        });
      });
  };

  useEffect(() => {
    getGroups();
  }, []);

  const handleComplete = (data) => {
    let fullAddress = data.address;
    let extraAddress = '';

    if (data.addressType === 'R') {
      if (data.bname !== '') {
        extraAddress += data.bname;
      }
      if (data.buildingName !== '') {
        extraAddress +=
          extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName;
      }
      fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
    }

    setValue('zipcode', data.zonecode);
    setValue('address1', fullAddress);
    setValue('address3', extraAddress);

    handleClose();
  };

  const onSubmit = async (data) => {
    const dataToSubmit = { ...data };
    if (dataToSubmit.birthday) {
      dataToSubmit.birthday = moment(birthday).format('YYYY-MM-DD');
    }
    
    await signup(dataToSubmit)
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            title: '회원 등록을 완료하였습니다.',
          }).then(() => {
            props.history.push('/admin/members/member');
          });
        }
      })
      .catch((error) => {
        console.error('error occured in CreateMember.js - handleSubmit() ', error);

        Swal.fire({
          icon: 'error',
          title: '회원을 등록할 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
        }).then(() => {
          return;
        });
      });
  };

  return (
    <>
      <SimpleHeader parentName="회원 설정" name="회원 등록" />
      <Container className="mt--6" fluid>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col>
              <Card>
                <Card.Body>
                  <div className="pl-lg-4 pr-lg-4 pt-4">
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        회원 아이디
                      </Form.Label>
                      <Col sm={4}>
                        <Form.Control
                          type="text"
                          {...register('userid', {
                            required: '아이디는 필수 입력 사항입니다.',
                            minLength: {
                              value: 4,
                              message: '아이디는 최소 네 자 이상 입력하세요.',
                            },
                            validate: async (value) => {
                              const result = await duplicatedId({ userid: value })
                                .then((response) => {
                                  if (response.data.success) {
                                    clearErrors('userid');
                                  }
                                  return response.data.success;
                                })
                                .catch((error) => {
                                  console.error(
                                    'error occured in CreateMember.js - checkDuplicatedId() ',
                                    error
                                  );
                                });
                              return result || '이미 사용 중인 아이디 입니다.'
                            },
                          })}
                          isInvalid={errors.userid}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.userid && errors.userid.message}
                        </Form.Control.Feedback>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        회원 이메일
                      </Form.Label>
                      <Col>
                        <Form.Control
                          type="email"
                          {...register('email', {
                            required: '이메일은 필수 입력 사항입니다.',
                            pattern: {
                              value: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/,
                              message: '이메일 형식에 맞지 않습니다.',
                            },
                            validate: async (value) => {
                              const result = await duplicatedEmail({ email: value })
                              .then((response) => {
                                if (response.data.success) {
                                  clearErrors('email');
                                }
                        
                                return response.data.success;
                              })
                              .catch((error) => {
                                console.error('error occured in CreateMember.js - checkDuplicatedEmail() ', error);
                              });
                              return result || '이미 사용 중인 이메일 입니다.';
                            },
                          })}
                          isInvalid={errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email && errors.email.message}
                        </Form.Control.Feedback>
                      </Col>
                      {/* <Col sm={2}>
                        <Form.Check
                          type="checkbox"
                          label="인증"
                          className="text-sm"
                          id="email_cert"
                          {...register('email_cert')}
                        />
                      </Col> */}
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Col sm={2}>
                        <Form.Label className="text-sm" htmlFor="usersName">
                          회원 본명
                        </Form.Label>
                      </Col>
                      <Col>
                        <Form.Control
                          type="text"
                          id="usersName"
                          {...register('username', {
                            required: '회원 본명은 필수 입력 사항입니다.',
                          })}
                          isInvalid={errors.username}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.username && errors.username.message}
                        </Form.Control.Feedback>
                      </Col>
                      <Col sm={1}>
                        <Form.Label className="text-sm" htmlFor="usersNickame">
                          닉네임
                        </Form.Label>
                      </Col>
                      <Col>
                        <Form.Control
                          type="text"
                          id="usersNickame"
                          {...register('nickname', {
                            required: '회원 별명은 필수 입력 사항입니다.',
                          })}
                          isInvalid={errors.nickname}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.nickname && errors.nickname.message}
                        </Form.Control.Feedback>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Col sm={2}>
                        <Form.Label className="text-sm" htmlFor="usersName">
                          비밀번호
                        </Form.Label>
                      </Col>
                      <Col>
                        <Form.Control
                          type="password"
                          id="password"
                          {...register('password', {
                            required: '비밀번호는 필수 입력 사항입니다.',
                            minLength: {
                              value: 4,
                              message: '비밀번호는 최소 네 자 이상 입력하세요.',
                            },
                          })}
                          isInvalid={errors.password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.password && errors.password.message}
                        </Form.Control.Feedback>
                      </Col>
                      <Col sm={1}>
                        <Form.Label className="text-sm" htmlFor="usersNickame">
                          비밀번호 확인
                        </Form.Label>
                      </Col>
                      <Col>
                        <Form.Control
                          type="password"
                          id="password2"
                          {...register('password2', {
                            required: '비밀번호 확인은 필수 입력 사항입니다.',
                            validate: (value) => value === getValues('password') || '비밀번호가 일치하지 않습니다.',
                          })}
                          isInvalid={errors.password2}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.password2 && errors.password2.message}
                        </Form.Control.Feedback>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        회원 그룹
                      </Form.Label>
                      <Col sm={2}>
                        <Form.Control
                          as="select"
                          custom
                          {...register('member_group')}
                        >
                          {groupList.map((group) => (
                            <option
                              value={group.id}
                              key={group.id + group.title}
                              onChange={() => setValue('member_group', group.id)}
                            >
                              {group.title}
                            </option>
                          ))}
                        </Form.Control>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        레벨
                      </Form.Label>
                      <Col sm={2}>
                        <Form.Control
                          type="number"
                          defaultValue={1}
                          {...register('level', {
                            valueAsNumber:
                              '1 이상 100 이하의 숫자만 입력 가능합니다.',
                            min: {
                              value: 1,
                              message:
                                '1 이상 100 이하의 숫자만 입력 가능합니다.',
                            },
                            max: {
                              value: 100,
                              message:
                                '1 이상 100 이하의 숫자만 입력 가능합니다.',
                            },
                          })}
                          isInvalid={errors.level}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.level && errors.level.message}
                        </Form.Control.Feedback>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        생년월일
                      </Form.Label>
                      <Col sm={4}>
                        <InputGroup hasValidation>
                          <ReactDatePicker myDate={birthday} setMyDate={setBirthday} required={false} maxDate={new Date(moment())} />
                        </InputGroup>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        전화 번호
                      </Form.Label>
                      <Col sm={4}>
                        <Form.Control
                          type="text"
                          {...register('phone', {
                            pattern: {
                              value: /^\d{11}$/,
                              message: '- 없이 열 한자리의 숫자로 입력하세요.',
                            },
                            validate: async (value) => {
                              if (value) {
                                const result = await duplicatedPhone({ phone: value })
                                  .then((response) => {
                                    if (response.data.success) {
                                      clearErrors('phone');
                                    }
                                    return response.data.success;
                                  })
                                  .catch((error) => {
                                    console.error('error occured in CreateMember.js - checkDuplicatedPhone() ', error);
                                  });
                                return result || '이미 사용 중인 전화 번호 입니다.';
                              }
                              return;
                            },
                          })}
                          isInvalid={errors.phone}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.phone && errors.phone.message}
                        </Form.Control.Feedback>
                      </Col>
                    </Form.Group>
                    <fieldset>
                      <Form.Group as={Row}>
                        <Form.Label
                          as="legend"
                          column
                          sm={2}
                          className="text-sm"
                        >
                          성별
                        </Form.Label>
                        <Col sm={10}>
                          <Form.Check
                            type="radio"
                            label="남자"
                            className="text-sm gender-radio"
                            name="sex"
                            id="1"
                            inline
                            onClick={() => setValue('sex', 1)}
                          />
                          <Form.Check
                            type="radio"
                            label="여자"
                            className="text-sm gender-radio"
                            name="sex"
                            id="2"
                            inline
                            onClick={() => setValue('sex', 2)}
                          />
                        </Col>
                      </Form.Group>
                    </fieldset>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        주소
                      </Form.Label>
                      <Col sm={10}>
                        <Row className="mb-2 align-items-center">
                          <Col sm="4">
                            <Form.Control
                              placeholder="우편번호"
                              id="postcode"
                              readOnly
                              {...register('zipcode')}
                            />
                          </Col>
                          <Col sm="4">
                            <Button
                              type="button"
                              variant="outline-default"
                              onClick={handleShow}
                            >
                              주소 찾기
                            </Button>
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col>
                            <Form.Control
                              placeholder="기본 주소"
                              id="address"
                              readOnly
                              {...register('address1')}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={6}>
                            <Form.Control
                              placeholder="상세 주소"
                              id="detailAddress"
                              {...register('address2')}
                              readOnly={
                                getValues('zipcode') !== '' ? false : true
                              }
                            />
                          </Col>
                          <Col xs={6}>
                            <Form.Control
                              placeholder="참고 주소"
                              id="extraAddress"
                              readOnly
                              {...register('address3')}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Form.Group>
                    {/* <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        메일 수신 여부
                      </Form.Label>
                      <Col sm={2}>
                        <Form.Check
                          type="checkbox"
                          label="수신"
                          className="text-sm"
                          id="receive_email"
                          {...register('receive_email')}
                        />
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        문자 수신 여부
                      </Form.Label>
                      <Col sm={2}>
                        <Form.Check
                          type="checkbox"
                          label="수신"
                          className="text-sm"
                          id="receive_sms"
                          {...register('receive_sms')}
                        />
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        차단 상태
                      </Form.Label>
                      <Col sm={2}>
                      <Form.Check
                          type="checkbox"
                          label="차단"
                          className="text-sm"
                          id="denied"
                          {...register('denied')}
                        />
                      </Col>
                    </Form.Group> */}
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        최고 관리자
                      </Form.Label>
                      <Col sm={2}>
                        <Form.Control
                          as="select"
                          custom
                          {...register('is_admin')}
                        >
                          <option value={0}>아닙니다.</option>
                          <option value={1}>최고 관리자입니다.</option>
                        </Form.Control>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="align-items-center">
                      <Form.Label column sm={2} className="text-sm">
                        관리자 메모
                      </Form.Label>
                      <Col sm={10}>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          {...register('adminmemo')}
                        />
                      </Col>
                    </Form.Group>
                  </div>
                  <div className="text-center">
                    <Button type="submit" variant="primary">
                      회원 등록
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        props.history.push('/admin/members/member');
                      }}
                    >
                      목록으로
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
      <Modal
        isOpen={show}
        toggle={() => setShow(!show)}
        centered
        size="lg"
        fade={false}
      >
        <ModalBody>
          <DaumPostcode onComplete={handleComplete} autoResize autoClose />
        </ModalBody>
      </Modal>
    </>
  );
}

export default withRouter(CreateMember);
